'use client';

import React, { useEffect, useRef, useState } from 'react';

import { teko } from '@/assets/fonts';

type VariantType = 'primary' | 'secondary' | 'danger' | 'neutral';
type ButtonSize = 'default' | 'small' | 'tiny'; // Added 'tiny' size option

type ColorScheme = {
  base: string;
  accent: string;
  hover: string;
  disabled: string;
  gradient: {
    stop1: string;
    stop2: string;
  };
};

type VariantsType = {
  [key in VariantType]: ColorScheme;
};

interface HexagonalButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: VariantType;
  color?: string;
  accentColor?: string;
  width?: number | string;
  height?: number;
  size?: ButtonSize;
  disabled?: boolean;
  label?: React.ReactNode;
  labelClassName?: string;
  textGradient?: string;
}

const VARIANTS: VariantsType = {
  primary: {
    base: '#3F4C89',
    accent: '#2B3A7E',
    hover: '#4F5C99',
    disabled: '#7A7A7A',
    gradient: {
      stop1: '#3F4C89',
      stop2: '#2B3A7E',
    },
  },
  secondary: {
    base: '#444444',
    accent: '#333333',
    hover: '#555555',
    disabled: '#7A7A7A',
    gradient: {
      stop1: '#444444',
      stop2: '#333333',
    },
  },
  danger: {
    base: '#3D2121',
    accent: '#FF5319',
    hover: '#4D2727',
    disabled: '#7A7A7A',
    gradient: {
      stop1: '#FF4D1D',
      stop2: '#DA292A',
    },
  },
  neutral: {
    base: '#363636',
    accent: '#6D6D6D',
    hover: '#464646',
    disabled: '#7A7A7A',
    gradient: {
      stop1: '#6D6D6D',
      stop2: '#6D6D6D',
    },
  },
};

// Size constants - added tiny size of 24px
const SIZES = {
  default: 52,
  small: 40,
  tiny: 24,
};

const HexagonalButton: React.FC<HexagonalButtonProps> = ({
  children,
  onClick,
  variant = 'primary',
  color,
  accentColor,
  width,
  height,
  size = 'default',
  className = '',
  disabled = false,
  label,
  labelClassName = '',
  textGradient,
  ...props
}) => {
  const [isHovered, setIsHovered] = useState<boolean>(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [svgWidth, setSvgWidth] = useState<number>((width as number) || 0);

  // Get the actual height based on size prop or explicit height
  const actualHeight = height || SIZES[size];

  const colorScheme = VARIANTS[variant];

  const baseColor = disabled ? colorScheme.disabled : isHovered ? colorScheme.hover : color || colorScheme.base;
  const bottomAccent = disabled ? '#777777' : accentColor || colorScheme.accent;

  // Set default width to 100% if not specified
  const buttonWidth = width || '100%';

  // Get a standard viewBox width for the SVG (for consistency in shape)
  const viewBoxWidth = typeof width === 'number' ? width : 275;

  // Calculate path coordinates based on the height ratio
  const heightRatio = actualHeight / 52; // Use the original height as reference
  const topLeftX = Math.round(15 * heightRatio);
  const topRightY = Math.round(8 * heightRatio);
  const bottomLeftY = Math.round(44 * heightRatio);
  const bottomRightX = Math.round(15 * heightRatio);
  const bottomLeftX = Math.round(8 * heightRatio);
  const topRightX = Math.round(8 * heightRatio);

  // Create the hexagonal path based on calculated coordinates
  const hexPath = `M${topLeftX} 0H${viewBoxWidth - topRightX}L${viewBoxWidth} ${topRightY}L${viewBoxWidth - bottomRightX} ${actualHeight}H${bottomLeftX}L0 ${bottomLeftY}L${topLeftX} 0Z`;

  // Calculate the accent bar at the bottom
  const bottomAccentY = Math.round(49 * heightRatio);
  const bottomAccentPath = `M${viewBoxWidth - Math.round(14 * heightRatio)} ${bottomAccentY}H${Math.round(5 * heightRatio)}L${bottomLeftX} ${actualHeight}H${viewBoxWidth - bottomRightX}L${viewBoxWidth - Math.round(14 * heightRatio)} ${bottomAccentY}Z`;

  useEffect(() => {
    // Update the SVG width when the button is mounted and on resize
    if (buttonRef.current && buttonWidth === '100%') {
      const updateWidth = () => {
        if (buttonRef.current) {
          setSvgWidth(buttonRef.current.offsetWidth);
        }
      };

      updateWidth();
      window.addEventListener('resize', updateWidth);
      return () => window.removeEventListener('resize', updateWidth);
    } else if (typeof buttonWidth === 'number') {
      setSvgWidth(buttonWidth);
    }
  }, [buttonWidth]);

  // Adjust font size based on button size
  const getFontSize = () => {
    if (size === 'tiny') return '16px';
    if (size === 'small') return '24px';
    return '32px';
  };

  const textBgStyle = {
    background: textGradient || 'linear-gradient(180deg, rgba(255, 255, 255, 0.7) 0%, #FFFFFF 100%)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    backgroundClip: 'text',
    fontFamily: `${teko.style.fontFamily} !important`,
    lineHeight: '100%',
    letterSpacing: 0,
    fontSize: getFontSize(), // Dynamic font size based on button size
  };

  // Fix for unique IDs to prevent conflicts when multiple buttons are on same page
  const uniqueId = React.useId();

  return (
    <button
      ref={buttonRef}
      onClick={disabled ? undefined : onClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      className={`relative flex items-center justify-center transition-all duration-300 bg-transparent border-none overflow-hidden
        ${disabled ? 'cursor-not-allowed' : 'cursor-pointer hover:shadow-lg active:scale-95'} 
        ${className}
        ${isHovered && !disabled ? 'transform scale-105' : ''}`}
      style={{ width: buttonWidth, height: actualHeight }}
      disabled={disabled}
      aria-label={typeof label === 'string' ? label : undefined}
      {...props}>
      <svg
        width='100%'
        height={actualHeight}
        viewBox={`0 0 ${viewBoxWidth} ${actualHeight}`}
        fill='none'
        xmlns='http://www.w3.org/2000/svg'
        className={`absolute inset-0 transition-all duration-300 ${disabled ? 'opacity-60' : ''}`}
        aria-hidden='true'
        preserveAspectRatio='none'>
        <mask id={`path-mask-${uniqueId}`} fill='white'>
          <path fillRule='evenodd' clipRule='evenodd' d={hexPath} />
        </mask>
        <path fillRule='evenodd' clipRule='evenodd' d={hexPath} fill={`url(#paint0_linear-${uniqueId})`} />
        <path
          fillRule='evenodd'
          clipRule='evenodd'
          d={hexPath}
          fill={baseColor}
          className='transition-colors duration-300'
        />
        <path
          d={`M${viewBoxWidth - topRightX} 0L${viewBoxWidth - Math.round(7.293 * heightRatio)} -${Math.round(0.707107 * heightRatio)}L${viewBoxWidth - Math.round(7.586 * heightRatio)} -1H${viewBoxWidth - topRightX}V0ZM${topLeftX} 0V-1H${Math.round(14.2844 * heightRatio)}L${Math.round(14.0535 * heightRatio)} -${Math.round(0.322674 * heightRatio)}L${topLeftX} 0ZM${viewBoxWidth} ${topRightY}L${viewBoxWidth + Math.round(0.947 * heightRatio)} ${Math.round((8.32267 / 52) * actualHeight)}L${viewBoxWidth + Math.round(1.147 * heightRatio)} ${Math.round((7.73324 / 52) * actualHeight)}L${viewBoxWidth + Math.round(0.707 * heightRatio)} ${Math.round((7.29289 / 52) * actualHeight)}L${viewBoxWidth} ${topRightY}ZM${viewBoxWidth - bottomRightX} ${actualHeight}V${Number(actualHeight) + 1}H${viewBoxWidth - Math.round(14.284 * heightRatio)}L${viewBoxWidth - Math.round(14.053 * heightRatio)} ${Math.round((52.3227 / 52) * actualHeight)}L${viewBoxWidth - bottomRightX} ${actualHeight}ZM${bottomLeftX} ${actualHeight}L${Math.round(7.29289 * heightRatio)} ${Math.round((52.7071 / 52) * actualHeight)}L${Math.round(7.58579 * heightRatio)} ${Number(actualHeight) + 1}H${bottomLeftX}V${actualHeight}ZM0 ${bottomLeftY}L-${Math.round(0.94651 * heightRatio)} ${Math.round((43.6773 / 52) * actualHeight)}L-${Math.round(1.14745 * heightRatio)} ${Math.round((44.2668 / 52) * actualHeight)}L-${Math.round(0.707107 * heightRatio)} ${Math.round((44.7071 / 52) * actualHeight)}L0 ${bottomLeftY}ZM${viewBoxWidth - topRightX} -1H${topLeftX}V1H${viewBoxWidth - topRightX}V-1ZM${viewBoxWidth + Math.round(0.707 * heightRatio)} ${Math.round((7.29289 / 52) * actualHeight)}L${viewBoxWidth - Math.round(7.293 * heightRatio)} -${Math.round(0.707107 * heightRatio)}L${viewBoxWidth - Math.round(8.707 * heightRatio)} ${Math.round(0.707107 * heightRatio)}L${viewBoxWidth - Math.round(0.293 * heightRatio)} ${Math.round((8.70711 / 52) * actualHeight)}L${viewBoxWidth + Math.round(0.707 * heightRatio)} ${Math.round((7.29289 / 52) * actualHeight)}ZM${viewBoxWidth - Math.round(0.947 * heightRatio)} ${Math.round((7.67733 / 52) * actualHeight)}L${viewBoxWidth - Math.round(15.947 * heightRatio)} ${Math.round((51.6773 / 52) * actualHeight)}L${viewBoxWidth - Math.round(14.053 * heightRatio)} ${Math.round((52.3227 / 52) * actualHeight)}L${viewBoxWidth + Math.round(0.947 * heightRatio)} ${Math.round((8.32267 / 52) * actualHeight)}L${viewBoxWidth - Math.round(0.947 * heightRatio)} ${Math.round((7.67733 / 52) * actualHeight)}ZM${bottomLeftX} ${Number(actualHeight) + 1}H${viewBoxWidth - bottomRightX}V${Number(actualHeight) - 1}H${bottomLeftX}V${Number(actualHeight) + 1}ZM-${Math.round(0.707107 * heightRatio)} ${Math.round((44.7071 / 52) * actualHeight)}L${Math.round(7.29289 * heightRatio)} ${Math.round((52.7071 / 52) * actualHeight)}L${Math.round(8.70711 * heightRatio)} ${Math.round((51.2929 / 52) * actualHeight)}L${Math.round(0.707107 * heightRatio)} ${Math.round((43.2929 / 52) * actualHeight)}L-${Math.round(0.707107 * heightRatio)} ${Math.round((44.7071 / 52) * actualHeight)}ZM${Math.round(14.0535 * heightRatio)} -${Math.round(0.322674 * heightRatio)}L-${Math.round(0.94651 * heightRatio)} ${Math.round((43.6773 / 52) * actualHeight)}L${Math.round(0.94651 * heightRatio)} ${Math.round((44.3227 / 52) * actualHeight)}L${Math.round(15.9465 * heightRatio)} ${Math.round(0.322674 * heightRatio)}L${Math.round(14.0535 * heightRatio)} -${Math.round(0.322674 * heightRatio)}`}
          fill={`url(#paint1_linear-${uniqueId})`}
          mask={`url(#path-mask-${uniqueId})`}
        />
        <mask
          id={`clip-mask-${uniqueId}`}
          style={{ maskType: 'alpha' }}
          maskUnits='userSpaceOnUse'
          x='0'
          y='0'
          width={viewBoxWidth}
          height={actualHeight}>
          <mask id={`path-inner-${uniqueId}`} fill='white'>
            <path fillRule='evenodd' clipRule='evenodd' d={hexPath} />
          </mask>
          <path fillRule='evenodd' clipRule='evenodd' d={hexPath} fill={`url(#paint2_linear-${uniqueId})`} />
          <path fillRule='evenodd' clipRule='evenodd' d={hexPath} fill='black' fillOpacity='0.2' />
          <path
            d={`M${viewBoxWidth - topRightX} 0L${viewBoxWidth - Math.round(7.293 * heightRatio)} -${Math.round(0.707107 * heightRatio)}L${viewBoxWidth - Math.round(7.586 * heightRatio)} -1H${viewBoxWidth - topRightX}V0ZM${topLeftX} 0V-1H${Math.round(14.2844 * heightRatio)}L${Math.round(14.0535 * heightRatio)} -${Math.round(0.322674 * heightRatio)}L${topLeftX} 0ZM${viewBoxWidth} ${topRightY}L${viewBoxWidth + Math.round(0.947 * heightRatio)} ${Math.round((8.32267 / 52) * actualHeight)}L${viewBoxWidth + Math.round(1.147 * heightRatio)} ${Math.round((7.73324 / 52) * actualHeight)}L${viewBoxWidth + Math.round(0.707 * heightRatio)} ${Math.round((7.29289 / 52) * actualHeight)}L${viewBoxWidth} ${topRightY}ZM${viewBoxWidth - bottomRightX} ${actualHeight}V${Number(actualHeight) + 1}H${viewBoxWidth - Math.round(14.284 * heightRatio)}L${viewBoxWidth - Math.round(14.053 * heightRatio)} ${Math.round((52.3227 / 52) * actualHeight)}L${viewBoxWidth - bottomRightX} ${actualHeight}ZM${bottomLeftX} ${actualHeight}L${Math.round(7.29289 * heightRatio)} ${Math.round((52.7071 / 52) * actualHeight)}L${Math.round(7.58579 * heightRatio)} ${Number(actualHeight) + 1}H${bottomLeftX}V${actualHeight}ZM0 ${bottomLeftY}L-${Math.round(0.94651 * heightRatio)} ${Math.round((43.6773 / 52) * actualHeight)}L-${Math.round(1.14745 * heightRatio)} ${Math.round((44.2668 / 52) * actualHeight)}L-${Math.round(0.707107 * heightRatio)} ${Math.round((44.7071 / 52) * actualHeight)}L0 ${bottomLeftY}ZM${viewBoxWidth - topRightX} -1H${topLeftX}V1H${viewBoxWidth - topRightX}V-1ZM${viewBoxWidth + Math.round(0.707 * heightRatio)} ${Math.round((7.29289 / 52) * actualHeight)}L${viewBoxWidth - Math.round(7.293 * heightRatio)} -${Math.round(0.707107 * heightRatio)}L${viewBoxWidth - Math.round(8.707 * heightRatio)} ${Math.round(0.707107 * heightRatio)}L${viewBoxWidth - Math.round(0.293 * heightRatio)} ${Math.round((8.70711 / 52) * actualHeight)}L${viewBoxWidth + Math.round(0.707 * heightRatio)} ${Math.round((7.29289 / 52) * actualHeight)}ZM${viewBoxWidth - Math.round(0.947 * heightRatio)} ${Math.round((7.67733 / 52) * actualHeight)}L${viewBoxWidth - Math.round(15.947 * heightRatio)} ${Math.round((51.6773 / 52) * actualHeight)}L${viewBoxWidth - Math.round(14.053 * heightRatio)} ${Math.round((52.3227 / 52) * actualHeight)}L${viewBoxWidth + Math.round(0.947 * heightRatio)} ${Math.round((8.32267 / 52) * actualHeight)}L${viewBoxWidth - Math.round(0.947 * heightRatio)} ${Math.round((7.67733 / 52) * actualHeight)}ZM${bottomLeftX} ${Number(actualHeight) + 1}H${viewBoxWidth - bottomRightX}V${Number(actualHeight) - 1}H${bottomLeftX}V${Number(actualHeight) + 1}ZM-${Math.round(0.707107 * heightRatio)} ${Math.round((44.7071 / 52) * actualHeight)}L${Math.round(7.29289 * heightRatio)} ${Math.round((52.7071 / 52) * actualHeight)}L${Math.round(8.70711 * heightRatio)} ${Math.round((51.2929 / 52) * actualHeight)}L${Math.round(0.707107 * heightRatio)} ${Math.round((43.2929 / 52) * actualHeight)}L-${Math.round(0.707107 * heightRatio)} ${Math.round((44.7071 / 52) * actualHeight)}ZM${Math.round(14.0535 * heightRatio)} -${Math.round(0.322674 * heightRatio)}L-${Math.round(0.94651 * heightRatio)} ${Math.round((43.6773 / 52) * actualHeight)}L${Math.round(0.94651 * heightRatio)} ${Math.round((44.3227 / 52) * actualHeight)}L${Math.round(15.9465 * heightRatio)} ${Math.round(0.322674 * heightRatio)}L${Math.round(14.0535 * heightRatio)} -${Math.round(0.322674 * heightRatio)}`}
            fill='white'
            fillOpacity='0.9'
            mask={`url(#path-inner-${uniqueId})`}
          />
        </mask>
        <g mask={`url(#clip-mask-${uniqueId})`}>
          <rect width={viewBoxWidth} height={actualHeight} fill={`url(#paint3_linear-${uniqueId})`} fillOpacity='0.5' />
          <rect
            width={viewBoxWidth}
            height={actualHeight}
            fillOpacity={isHovered && !disabled ? '0.2' : '0.1'}
            fill='white'
            className='transition-opacity duration-300'
          />
        </g>
        <path
          fillRule='evenodd'
          clipRule='evenodd'
          d={bottomAccentPath}
          fill={bottomAccent}
          className='transition-colors duration-300'
        />
        <path
          fillRule='evenodd'
          clipRule='evenodd'
          d={`M${Math.round(110 * (actualHeight / 52))} ${Math.round(3 * heightRatio)}H${Math.round(168 * (actualHeight / 52))}L${Math.round(171 * (actualHeight / 52))} 0H${Math.round(107 * (actualHeight / 52))}L${Math.round(110 * (actualHeight / 52))} ${Math.round(3 * heightRatio)}Z`}
          fill='white'
          fillOpacity={disabled ? '0.05' : '0.1'}
          className='transition-opacity duration-300'
        />
        <defs>
          <linearGradient
            id={`paint0_linear-${uniqueId}`}
            x1={viewBoxWidth / 2}
            y1='0'
            x2={viewBoxWidth / 2}
            y2={actualHeight}
            gradientUnits='userSpaceOnUse'>
            <stop stopColor='#333333' stopOpacity='0.8' />
            <stop offset='1' stopColor='#111111' stopOpacity='0.9' />
          </linearGradient>
          <linearGradient
            id={`paint1_linear-${uniqueId}`}
            x1={viewBoxWidth / 2}
            y1='0'
            x2={viewBoxWidth / 2}
            y2={actualHeight}
            gradientUnits='userSpaceOnUse'>
            <stop stopColor='white' stopOpacity={isHovered && !disabled ? '0.2' : '0.1'} />
            <stop offset='1' stopColor={disabled ? '#777777' : bottomAccent} />
          </linearGradient>
          <linearGradient
            id={`paint2_linear-${uniqueId}`}
            x1={viewBoxWidth / 2}
            y1='0'
            x2={viewBoxWidth / 2}
            y2={actualHeight}
            gradientUnits='userSpaceOnUse'>
            <stop stopColor='#383838' stopOpacity='0.8' />
            <stop offset='1' stopColor='#1F1F1F' stopOpacity='0.9' />
          </linearGradient>
          <linearGradient
            id={`paint3_linear-${uniqueId}`}
            x1={viewBoxWidth / 2}
            y1='0'
            x2={viewBoxWidth / 2}
            y2={actualHeight}
            gradientUnits='userSpaceOnUse'>
            <stop
              stopColor={disabled ? '#777777' : colorScheme.gradient.stop1}
              stopOpacity={isHovered && !disabled ? '0.2' : '0'}
            />
            <stop offset='1' stopColor={disabled ? '#777777' : colorScheme.gradient.stop2} />
          </linearGradient>
        </defs>
      </svg>

      {/* Label with gradient text effect */}
      {label && (
        <span
          className={`relative z-10 font-regular tracking-wider transition-all duration-300 ${disabled ? 'opacity-60' : ''} ${labelClassName}`}
          style={textBgStyle}>
          {label}
        </span>
      )}

      {/* Children content - parent controls styling */}
      {children && <div className='relative z-10'>{children}</div>}
    </button>
  );
};

export default HexagonalButton;
